import type { ReviewEvidence } from '@console/types'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WHICH KIND OF TRUST THIS EVIDENCE CARRIES — `LGH-036`, `SPEC-CPM-001 §8`.
 *
 * `SPEC-CPM-001 §8` distinguishes tenant-scoped memory (this tenant's own
 * catalogue, or something this tenant taught the drafter on an earlier quote)
 * from shared world knowledge (a suggestion learned across other tenants,
 * never this one's own confirmed fact). Rendering both the same way — same
 * sentence, same weight — is exactly the "merged presentation misstates
 * confidence" failure the task names: a value read from a customer's own
 * catalogue and a value guessed from strangers' history are not the same
 * claim, and a screen that shows them identically tells the operator they
 * are.
 *
 * This is presentation over a class the platform already distinguishes
 * structurally (`evidence.kind`) — nothing here infers or reclassifies
 * anything; it only picks the label a human reads for a kind that already
 * exists on the wire.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function evidenceClassLabel(evidence: ReviewEvidence): string {
  switch (evidence.kind) {
    case 'catalog':
      return 'Your catalogue'
    case 'tenantVocabulary':
      return 'You taught us this'
    case 'sharedSuggestion':
      return 'Learned from other customers'
    case 'rfqSpan':
      return "The customer's own words"
    case 'operatorDecision':
      return 'Decided on this quote'
  }
}

/**
 * Tenant-scoped memory (this tenant's own catalogue or corrections) versus
 * everything else. `sharedSuggestion` is the one arm evidenced by other
 * tenants' history — the lower-trust case `§8` asks to be visually distinct,
 * not just differently worded.
 */
export function isSharedEvidence(evidence: ReviewEvidence): boolean {
  return evidence.kind === 'sharedSuggestion'
}
